import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

import { notifyPasswordChangeRequired, notifySessionExpired } from './session-expired';

const HOST_GROWME = process.env.REACT_APP_HOST_GROWME as string;

export const HOST_PUBLIC = process.env.REACT_APP_HOST_PUBLIC as string;

// Ruta del mismo dominio del front que Vercel reenvía al back (rewrites en
// vercel.json). Sirve cuando la red de la persona (firewall o proxy de empresa)
// deja abrir la página pero bloquea el dominio del back: el pedido sale hacia el
// mismo dominio que ya cargó. Sin entrada (p. ej. localhost) no hay alternativa.
const BACKEND_PROXY_PATHS: Record<string, string> = {
  'https://superadmin-back.growmeapp.ai': '/_backend/prod',
  'https://superadmin-back-dev.growmeapp.ai': '/_backend/dev',
};
const BACKEND_PROXY_PATH = BACKEND_PROXY_PATHS[(HOST_GROWME || '').replace(/\/+$/, '')];

let useBackendProxy = false;

export const canUseBackendProxy = (): boolean => !!BACKEND_PROXY_PATH;

export const isUsingBackendProxy = (): boolean => useBackendProxy;

// Base actual del back (directa o por el proxy), para pedidos que no pasan por axios.
export const getBackendBaseUrl = (): string => (useBackendProxy ? BACKEND_PROXY_PATH : HOST_GROWME);

export const getBackendProxyPath = (): string | undefined => BACKEND_PROXY_PATH;

export const getBackendDirectUrl = (): string => HOST_GROWME;

// Desde aquí en adelante (hasta recargar la página) todos los pedidos van por el proxy.
// La página pública del test lo activa al cargar: así usa un solo dominio
// (app.growmeapp.ai), el mismo que la red ya dejó abrir, y las redes de empresa
// que bloquean el dominio del back no la afectan.
export const enableBackendProxy = (): void => {
  if (BACKEND_PROXY_PATH) useBackendProxy = true;
};

export const disableBackendProxy = (): void => {
  useBackendProxy = false;
};

// Sin respuesta del back (red, bloqueo o timeout), a diferencia de un error HTTP
// que sí respondió (link inválido, test eliminado). 502-504 y 407 los arma un
// intermediario (proxy de empresa o balanceador), no el back.
export const isConnectionError = (error: any): boolean => {
  const status = error?.response?.status;
  return !error?.response || status === 407 || status === 502 || status === 503 || status === 504;
};

interface TokenData {
  accessToken: string;
}

const getAccessToken = async (): Promise<TokenData> => {
  return new Promise((resolve, reject) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return reject({ accessToken: "" });
    }

    resolve({ accessToken: token });
  });
}

// El back solo responde 401 cuando el token falta, es inválido o expiró
// (authMiddleware / AuthValidateSessionController), y no hay refresh token:
// reintentar con el mismo token no sirve, así que se cierra la sesión.
// /login y /validateSession manejan su propio 401 (credenciales incorrectas y
// chequeo inicial en AllRoutes).
const handleResponseError = (error: any): Promise<AxiosError> => {
  const url: string = error.config?.url || '';
  const isAuthRequest = url.includes('/validateSession') || url.includes('/login');

  if (error.response?.status === 401 && !isAuthRequest) {
    notifySessionExpired();
  }
  if (error.response?.status === 403 && error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
    notifyPasswordChangeRequired();
  }
  console.error('Axios interceptor error', error);
  return Promise.reject(error);
};

// Las instancias de axios.create() no heredan los interceptors del singleton,
// por eso se registra el mismo handler en cada una.
const createClient = (accessToken: string): AxiosInstance => {
  const client = axios.create({
    baseURL: useBackendProxy ? BACKEND_PROXY_PATH : HOST_GROWME,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken ? `Bearer ${accessToken}` : '',
      // Solo por el proxy (mismo dominio, sin preflight CORS): el back lo anota en el log.
      ...(useBackendProxy ? { 'X-GrowMe-Via': 'proxy' } : {}),
    },
  });
  client.interceptors.response.use((response) => response, handleResponseError);
  return client;
}

const initializeClient = async (): Promise<AxiosInstance> => {
  try {
    const { accessToken } = await getAccessToken();

    return createClient(accessToken);
  } catch (error) {
    console.error('Error initializing Axios client:', error);
    //@ts-ignore
    if (error.accessToken === "") {
      return createClient('');
    }
    throw new AxiosError('Error initializing Axios client');
  }
}

axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  handleResponseError
);

const methods = {
  get: async (url: string, params?: any) => (await initializeClient()).get(url, { params }),
  // proxyFallback: si el back no responde por la vía en uso se reintenta por la otra
  // (directa ↔ proxy del front) y, si así funciona, el resto de la sesión sigue por
  // ahí. Hay redes de empresa que dejan pasar la carga del test pero cortan los
  // guardados (pedidos más grandes): sin esto el avance se perdía en silencio.
  post: async (url: string, body?: any, config?: any) => {
    const { params, headers, timeout, proxyFallback } = config || {};
    const send = async () => (await initializeClient()).post(url, body, { params, headers, timeout });
    if (!proxyFallback || !BACKEND_PROXY_PATH) return send();
    try {
      return await send();
    } catch (error) {
      if (!isConnectionError(error)) throw error;
      const wasUsingProxy = useBackendProxy;
      console.warn(`El back no respondió (${url}) por ${wasUsingProxy ? 'el proxy' : 'la vía directa'}, reintentando por la otra vía`, error);
      if (wasUsingProxy) disableBackendProxy(); else enableBackendProxy();
      try {
        return await send();
      } catch (retryError) {
        // Tampoco por la otra vía: se vuelve a la preferida para el próximo intento.
        if (wasUsingProxy) enableBackendProxy(); else disableBackendProxy();
        throw retryError;
      }
    }
  },
  put: async (url: string, body?: any, params?: any) => (await initializeClient()).put(url, body, { params }),
  del: async (url: string, body?: any, params?: any) => (await initializeClient()).delete(url, { data: body, params }),
  download: async (url: string, params?: any) => (await initializeClient()).get(url, { 
    params, 
    responseType: 'arraybuffer' 
  }),
};

export { methods };
